import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { getBaseUrl } from "../../../../Redux/Config/AppConfig";
import NightAuditSummary from "./NightAuditSummary";

const NightAudit = () => {
  const [auditDate, setAuditDate] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [isRunning, setIsRunning] = useState(false);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState("success"); // success, error, info
  const [progress, setProgress] = useState(0);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [auditInfo, setAuditInfo] = useState(null);
  const [report, setReport] = useState(null);

  // Get user data from Redux store (adjust the path according to your store structure)
  const { user } = useSelector((state) => state.auth || {});
  const isAdmin =
    user?.role === "admin" ||
    user?.is_admin === true ||
    user?.user_type === "admin";

  const BaseUrl = getBaseUrl();

  // Simulate progress during audit
  useEffect(() => {
    let interval;
    if (isRunning) {
      interval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 90) return prev;
          return prev + Math.random() * 15;
        });
      }, 500);
    } else {
      setProgress(0);
    }
    return () => clearInterval(interval);
  }, [isRunning]);

  useEffect(() => {
    async function fetchAuditInfo() {
      try {
        let token = localStorage.getItem("token");
        let hotel_id = localStorage.getItem("hotel_id");
        const res = await fetch(
          `${BaseUrl}/night-audit/latest-log?hotel_id=${hotel_id}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const data = await res.json();
        if (data.success) {
          setAuditInfo(data.data);
          setReport(data.data.report);
        }
      } catch (e) {
        // ignore
      }
    }
    fetchAuditInfo();
  }, [isRunning, message]);

  const runNightAudit = async () => {
    setIsRunning(true);
    setMessage(null);
    setProgress(0);
    setShowConfirmModal(false);

    try {
      let token = localStorage.getItem("token");
      let hotel_id = localStorage.getItem("hotel_id");

      const response = await fetch(`${BaseUrl}/night-audit/run`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ auditDate, hotel_id }),
      });
      const result = await response.json();

      // Complete the progress
      setProgress(100);

      if (result.success) {
        setMessage("Night audit completed successfully!");
        setMessageType("success");
      } else {
        setMessage("Night audit failed: " + (result.error || "Unknown error"));
        setMessageType("error");
      }
    } catch (error) {
      setProgress(100);
      setMessage("Night audit failed: " + error.message);
      setMessageType("error");
    } finally {
      setTimeout(() => {
        setIsRunning(false);
        setProgress(0);
      }, 1000);
    }
  };

  const handleRunAudit = () => {
    setShowConfirmModal(true);
  };

  // Helper to render key-value pairs as a list
  const renderList = (obj) => (
    <ul>
      {Object.entries(obj || {}).map(([key, value]) => (
        <li key={key}>
          <strong>{key}:</strong>{" "}
          {typeof value === "object" ? JSON.stringify(value) : value}
        </li>
      ))}
    </ul>
  );

  // Helper to render activity log as a table
  const renderActivityLog = (activityLog) => {
    if (
      !activityLog ||
      !Array.isArray(activityLog.staffActivity) ||
      activityLog.staffActivity.length === 0
    ) {
      return <div className="text-muted">No staff activity found.</div>;
    }
    return (
      <div className="table-responsive mb-3">
        <table className="table table-bordered table-sm">
          <thead className="table-light">
            <tr>
              <th>Staff</th>
              <th>Role</th>
              <th>Action</th>
              <th>Timestamp</th>
              <th>Remarks</th>
            </tr>
          </thead>
          <tbody>
            {activityLog.staffActivity.map((a, idx) => (
              <tr key={idx}>
                <td>{a.staffName}</td>
                <td>{a.staffRole}</td>
                <td>{a.action}</td>
                <td>
                  {a.timestamp ? new Date(a.timestamp).toLocaleString() : "-"}
                </td>
                <td>{a.remarks}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Helper to render actions as a list
  const renderActions = (actions) => (
    <ul>
      {(actions || []).map((action, idx) => (
        <li key={idx}>{action}</li>
      ))}
    </ul>
  );

  // Helper to render generated reports
  const renderGeneratedReports = (generatedReports) => (
    <>
      <ul>
        {(generatedReports?.generatedReports || []).map((r, idx) => (
          <li key={idx}>{r}</li>
        ))}
      </ul>
      {generatedReports?.generatedAt && (
        <div className="text-muted small">
          Generated At:{" "}
          {new Date(generatedReports.generatedAt).toLocaleString()}
        </div>
      )}
    </>
  );

  // Don't render if user is not admin
  if (!isAdmin) {
    return null;
  }

  return (
    <>
      <div className="card shadow-sm border-0 mb-4">
        <div className="card-header bg-gradient-primary text-white border-0">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <div className="bg-dark bg-opacity-20 rounded-circle p-2 me-3">
                <i className="bi bi-moon-stars fs-4"></i>
              </div>
              <div>
                <h5 className="mb-0 fw-bold text-black">Night Audit System</h5>
                <small className="opacity-75 text-black">
                  Administrative Control Panel
                </small>
              </div>
            </div>
            {/* <div className="badge bg-white text-primary px-3 py-2">
              <i className="bi bi-shield-check me-1"></i>
              Admin Only
            </div> */}
          </div>
        </div>

        <div className="card-body p-4">
          {/* Warning Banner */}
          <div className="alert alert-warning border-0 mb-4">
            <div className="d-flex align-items-center">
              <i className="bi bi-exclamation-triangle-fill fs-5 me-3 text-warning"></i>
              <div>
                <strong>Important:</strong> Night audit will process all daily
                transactions and update system records. This operation cannot be
                undone.
              </div>
            </div>
          </div>

          <div className="row g-4">
            {/* Date Selection */}
            <div className="col-lg-6">
              <div className="bg-light rounded-3 p-4 h-100">
                <div className="d-flex align-items-center mb-3">
                  <i className="bi bi-calendar-event text-primary fs-5 me-2"></i>
                  <h6 className="mb-0 fw-bold">Audit Configuration</h6>
                </div>
                <label className="form-label fw-semibold text-muted mb-2">
                  Select Audit Date
                </label>
                <input
                  type="date"
                  className="form-control form-control-lg border-0 shadow-sm"
                  value={auditDate}
                  onChange={(e) => setAuditDate(e.target.value)}
                  disabled={isRunning}
                  max={new Date().toISOString().slice(0, 10)}
                />
                <small className="text-muted mt-2 d-block">
                  <i className="bi bi-info-circle me-1"></i>
                  Selected date:{" "}
                  {new Date(auditDate).toLocaleDateString("en-US", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </small>
              </div>
            </div>

            {/* Action Panel */}
            <div className="col-lg-6">
              <div className="bg-light rounded-3 p-4 h-100">
                <div className="d-flex align-items-center mb-3">
                  <i className="bi bi-gear-fill text-primary fs-5 me-2"></i>
                  <h6 className="mb-0 fw-bold">Audit Control</h6>
                </div>
                <div className="d-grid gap-3">
                  <button
                    className={`btn btn-lg ${
                      isRunning ? "btn-secondary" : "btn-primary"
                    } shadow-sm`}
                    onClick={handleRunAudit}
                    disabled={isRunning}
                  >
                    {isRunning ? (
                      <>
                        <div
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        Processing Audit...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-play-circle-fill me-2"></i>
                        Run Night Audit
                      </>
                    )}
                  </button>

                  {/* Progress Bar */}
                  {isRunning && (
                    <div className="mt-2">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <small className="text-muted fw-semibold">
                          Progress
                        </small>
                        <small className="text-muted fw-semibold">
                          {Math.round(progress)}%
                        </small>
                      </div>
                      <div className="progress" style={{ height: "8px" }}>
                        <div
                          className="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                          role="progressbar"
                          style={{ width: `${progress}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Status Message */}
          {message && (
            <div className="mt-4">
              <div
                className={`alert ${
                  messageType === "success"
                    ? "alert-success"
                    : messageType === "error"
                    ? "alert-danger"
                    : "alert-info"
                } border-0 shadow-sm`}
              >
                <div className="d-flex align-items-center">
                  <i
                    className={`${
                      messageType === "success"
                        ? "bi bi-check-circle-fill text-success"
                        : messageType === "error"
                        ? "bi bi-x-circle-fill text-danger"
                        : "bi bi-info-circle-fill text-info"
                    } fs-5 me-3`}
                  ></i>
                  <div>
                    <strong>
                      {messageType === "success"
                        ? "Success!"
                        : messageType === "error"
                        ? "Error!"
                        : "Info:"}
                    </strong>
                    <div>{message}</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Audit Information */}
          <div className="mt-4 pt-4 border-top">
            <div className="row g-3">
              <div className="col-md-4">
                <div className="text-center">
                  <i className="bi bi-clock-history text-muted fs-4 mb-2 d-block"></i>
                  <small className="text-muted fw-semibold">Last Run</small>
                  <div className="fw-bold">
                    {auditInfo
                      ? new Date(auditInfo.created_at).toLocaleString()
                      : "No data"}
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="text-center">
                  <i className="bi bi-calendar-check text-muted fs-4 mb-2 d-block"></i>
                  <small className="text-muted fw-semibold">Status</small>
                  <div
                    className={`fw-bold ${
                      auditInfo?.status === "completed"
                        ? "text-success"
                        : "text-danger"
                    }`}
                  >
                    {auditInfo?.status || "No data"}
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="text-center">
                  <i className="bi bi-clipboard-data text-muted fs-4 mb-2 d-block"></i>
                  <small className="text-muted fw-semibold">Records</small>
                  <div className="fw-bold">
                    {auditInfo?.records_processed ?? "No data"}
                  </div>
                </div>
              </div>
            </div>

            {/* Detailed Night Audit Report for Admins */}
            {isAdmin && auditInfo && (
              <div className="mt-5">
                <h5 className="fw-bold mb-3">Night Audit Report Details</h5>
                <div className="card p-3 mb-3">
                  <div>
                    <strong>Date:</strong>{" "}
                    {auditInfo.audit_date
                      ? new Date(auditInfo.audit_date).toLocaleDateString()
                      : "-"}
                  </div>
                  <div>
                    <strong>Status:</strong> {auditInfo.status || "-"}
                  </div>
                  <div>
                    <strong>Records Processed:</strong>{" "}
                    {auditInfo.records_processed ?? "-"}
                  </div>
                  <div>
                    <strong>Message:</strong> {auditInfo.message || "-"}
                  </div>
                </div>
              </div>
            )}
            {report && (
              // <div>
              //   <section className="mb-3">
              //     <h3>Room & Reservation Summary</h3>
              //     {renderList(report.roomSummary)}
              //   </section>
              //   <section className="mb-3">
              //     <h3>Revenue Summary</h3>
              //     {renderList(report.revenueSummary)}
              //   </section>
              //   <section className="mb-3">
              //     <h3>Payment Summary</h3>
              //     {renderList(report.paymentSummary)}
              //   </section>
              //   <section className="mb-3">
              //     <h3>Guest Ledger</h3>
              //     {Array.isArray(report.guestLedger?.guestLedger) &&
              //     report.guestLedger.guestLedger.length > 0 ? (
              //       <pre className="bg-light p-2 rounded small mb-0">
              //         {JSON.stringify(report.guestLedger.guestLedger, null, 2)}
              //       </pre>
              //     ) : (
              //       <div className="text-muted">No guest ledger entries.</div>
              //     )}
              //   </section>
              //   <section className="mb-3">
              //     <h3>Inventory Usage</h3>
              //     {Array.isArray(report.inventoryUsage?.usageSummary) &&
              //     report.inventoryUsage.usageSummary.length > 0 ? (
              //       <pre className="bg-light p-2 rounded small mb-0">
              //         {JSON.stringify(
              //           report.inventoryUsage.usageSummary,
              //           null,
              //           2
              //         )}
              //       </pre>
              //     ) : (
              //       <div className="text-muted">
              //         No inventory usage entries.
              //       </div>
              //     )}
              //   </section>
              //   <section className="mb-3">
              //     <h3>Front Office Activity Log</h3>
              //     {renderActivityLog(report.activityLog)}
              //   </section>
              //   <section className="mb-3">
              //     <h3>Date Rollover</h3>
              //     {renderActions(report.dateRollover?.actions)}
              //   </section>
              //   <section className="mb-3">
              //     <h3>System Locking</h3>
              //     {renderActions(report.systemLocking?.actions)}
              //   </section>
              //   <section className="mb-3">
              //     <h3>Generated Reports</h3>
              //     {renderGeneratedReports(report.generatedReports)}
              //   </section>
              // </div>
              <NightAuditSummary report={report}/>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow">
              <div className="modal-header bg-warning text-dark border-0">
                <h5 className="modal-title fw-bold">
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  Confirm Night Audit
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowConfirmModal(false)}
                ></button>
              </div>
              <div className="modal-body p-4">
                <p className="mb-3">
                  You are about to run the night audit for{" "}
                  <strong>
                    {new Date(auditDate).toLocaleDateString("en-US", {
                      weekday: "long",
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </strong>
                  .
                </p>
                <div className="alert alert-warning border-0">
                  <ul className="mb-0 ps-3">
                    <li>This will process all daily transactions</li>
                    <li>System reports will be updated</li>
                    <li>This operation cannot be reversed</li>
                  </ul>
                </div>
                <p className="mb-0">Are you sure you want to continue?</p>
              </div>
              <div className="modal-footer border-0">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setShowConfirmModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={runNightAudit}
                >
                  <i className="bi bi-play-circle-fill me-2"></i>
                  Run Audit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NightAudit;
